import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import ProductCard from '../../components/base/ProductCard';
import SEO from '../../components/SEO';
import { fetchProduct, fetchProducts, formatPrice, type Product } from '../../utils/api';
import { absoluteUrl, getProductPath, getProductUrl } from '../../utils/urls';
import { getCategoryPathByName } from '../../data/categories';

export default function ProductDetail() {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProduct() {
      if (!id) return;

      setLoading(true);
      try {
        const response = await fetchProduct(id);
        if (response.success && response.data.product) {
          const loaded = response.data.product;
          setProduct(loaded);

          const canonicalPath = getProductPath(loaded);
          const currentPath = slug ? `/produkt/${loaded.id}/${slug}` : `/produkt/${loaded.id}`;
          if (canonicalPath !== currentPath) {
            navigate(canonicalPath, { replace: true });
          }

          if (loaded.category) {
            const relatedResponse = await fetchProducts({
              category: loaded.category,
              limit: 4
            });
            const filtered = relatedResponse.data.products.filter(p => p.id !== loaded.id);
            setRelatedProducts(filtered.slice(0, 4));
          }
        } else {
          console.error('Invalid response from API:', response);
        }
      } catch (error) {
        console.error('Failed to load product:', error);
      } finally {
        setLoading(false);
      }
    }

    loadProduct();
  }, [id, slug, navigate]);

  const handleGoToShop = () => {
    if (product?.product_url) {
      window.open(product.product_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <p className="text-gray-600">Indlæser produkt...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO
          title="Produktet blev ikke fundet"
          description="Produktet findes ikke eller er ikke længere på lager."
          robots="noindex, follow"
        />
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Produktet blev ikke fundet</h1>
          <Button onClick={() => navigate('/')}>Tilbage til forsiden</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryPath = getCategoryPathByName(product.category);
  const productUrl = getProductUrl(product);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={product.title}
        description={product.description || `Sammenlign pris på ${product.title} fra ${product.shop}. ${product.brand || ''} – ${product.category || 'hesteudstyr'}.`}
        keywords={`${product.title}, ${product.brand || ''}, ${product.category || ''}, hesteudstyr, ${product.shop || ''}`}
        image={product.image_url}
        url={productUrl}
        type="product"
        breadcrumbs={[
          { name: 'Hjem', url: absoluteUrl('/') },
          { name: product.category || 'Produkter', url: absoluteUrl(categoryPath) },
          { name: product.title, url: productUrl }
        ]}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.title,
          image: product.image_url,
          description: product.description,
          sku: product.sku || undefined,
          brand: {
            '@type': 'Brand',
            name: product.brand
          },
          offers: {
            '@type': 'Offer',
            url: productUrl,
            priceCurrency: product.currency,
            price: product.price,
            priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            itemCondition: 'https://schema.org/NewCondition',
            availability: product.in_stock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            seller: {
              '@type': 'Organization',
              name: product.shop
            }
          }
        }}
      />
      <Header />
      <main>
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav aria-label="Breadcrumb" className="flex items-center space-x-2 text-sm">
              <Link to="/" className="text-emerald-600 hover:text-emerald-700">
                Hjem
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400"></i>
              <Link to={categoryPath} className="text-emerald-600 hover:text-emerald-700">
                {product.category || 'Kategori'}
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400"></i>
              <span className="text-gray-500 truncate max-w-xs">{product.title}</span>
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-4">
              <div className="aspect-square bg-white rounded-lg shadow-md overflow-hidden">
                <img
                  src={product.image_url || 'https://via.placeholder.com/600x600?text=No+Image'}
                  alt={product.title}
                  width="600"
                  height="600"
                  className="w-full h-full object-cover object-top"
                  fetchPriority="high"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.title}</h1>
                <div className="flex items-center space-x-3 mb-4 flex-wrap">
                  <span className="text-3xl font-bold text-emerald-600">{formatPrice(product.price, product.currency)}</span>
                  {product.old_price && product.old_price > product.price && (
                    <>
                      <span className="text-xl text-gray-500 line-through">{formatPrice(product.old_price, product.currency)}</span>
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded-md text-sm font-medium">
                        Spar {Math.round(((product.old_price - product.price) / product.old_price) * 100)}%
                      </span>
                    </>
                  )}
                </div>
                <p className="text-gray-600 mb-4">
                  <span className="font-medium">Mærke:</span> {product.brand} |
                  <span className="font-medium ml-2">Shop:</span> {product.shop}
                </p>
                <Link
                  to={categoryPath}
                  className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium mb-4 hover:bg-emerald-200"
                >
                  {product.category}
                </Link>
              </div>

              <div className="border-t pt-6">
                <p className="text-gray-700 leading-relaxed mb-6">{product.description || 'Se produktinformation hos forhandler.'}</p>

                <div className="flex items-center space-x-2 mb-6">
                  {product.in_stock ? (
                    <>
                      <i className="ri-checkbox-circle-fill text-green-500"></i>
                      <span className="text-green-600 font-medium">
                        På lager{product.stock_count ? ` (${product.stock_count} tilgængelige)` : ''}
                      </span>
                    </>
                  ) : (
                    <>
                      <i className="ri-close-circle-fill text-red-500"></i>
                      <span className="text-red-600 font-medium">Udsolgt</span>
                    </>
                  )}
                </div>

                <div className="space-y-3">
                  <Button
                    size="lg"
                    onClick={handleGoToShop}
                    disabled={!product.in_stock}
                    className="w-full"
                  >
                    <i className="ri-external-link-line mr-2"></i>
                    {product.in_stock ? `Gå til butik - ${product.shop}` : 'Udsolgt hos forhandler'}
                  </Button>
                  {product.in_stock ? (
                    <p className="text-sm text-gray-500 text-center">
                      Du vil blive omdirigeret til {product.shop} for at gennemføre dit køb
                    </p>
                  ) : (
                    <p className="text-sm text-red-500 text-center">
                      Dette produkt er desværre udsolgt. Tjek igen senere eller se relaterede produkter nedenfor.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {product.material && (
            <div className="mt-12 bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Produktinformation</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {product.material && (
                  <div className="flex items-start space-x-3">
                    <i className="ri-palette-line text-emerald-500 mt-0.5"></i>
                    <div>
                      <p className="text-sm text-gray-600">Materiale</p>
                      <p className="font-medium text-gray-900">{product.material}</p>
                    </div>
                  </div>
                )}
                {product.brand && (
                  <div className="flex items-start space-x-3">
                    <i className="ri-shield-check-line text-emerald-500 mt-0.5"></i>
                    <div>
                      <p className="text-sm text-gray-600">Mærke</p>
                      <p className="font-medium text-gray-900">{product.brand}</p>
                    </div>
                  </div>
                )}
                {product.sku && (
                  <div className="flex items-start space-x-3">
                    <i className="ri-barcode-line text-emerald-500 mt-0.5"></i>
                    <div>
                      <p className="text-sm text-gray-600">Varenummer</p>
                      <p className="font-medium text-gray-900">{product.sku}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {relatedProducts.length > 0 && (
            <div className="mt-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Relaterede Produkter</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map((relatedProduct) => (
                  <ProductCard
                    key={relatedProduct.id}
                    href={getProductPath(relatedProduct)}
                    name={relatedProduct.title}
                    price={formatPrice(relatedProduct.price, relatedProduct.currency)}
                    originalPrice={relatedProduct.old_price && relatedProduct.old_price > relatedProduct.price ? formatPrice(relatedProduct.old_price, relatedProduct.currency) : undefined}
                    image={relatedProduct.image_url || 'https://via.placeholder.com/300x300?text=No+Image'}
                    vendor={relatedProduct.shop || relatedProduct.brand}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
